import { useQuery } from "@tanstack/react-query";
import {
  DEFAULT_LOCALE,
  WHATS_NEW_INDEX_URL,
  entryMarkdownUrl,
} from "../config";
import type { WhatsNewEntry, WhatsNewIndex, WhatsNewLocale } from "../types";

const STALE_TIME = 5 * 60 * 1000;
const CACHE_TIME = 60 * 60 * 1000;

const fetchIndex = async (): Promise<WhatsNewEntry[]> => {
  const res = await fetch(WHATS_NEW_INDEX_URL);
  if (!res.ok) {
    throw new Error(`Failed to load What's New index (${res.status})`);
  }
  const json = (await res.json()) as WhatsNewIndex;
  return [...(json.entries ?? [])].sort((a, b) => b.id - a.id);
};

const fetchEntryContent = async (
  id: number,
  locale: WhatsNewLocale
): Promise<string> => {
  const res = await fetch(entryMarkdownUrl(id, locale));
  if (res.ok) return res.text();

  // Fall back to the default locale if the requested one is missing.
  if (res.status === 404 && locale !== DEFAULT_LOCALE) {
    const fallback = await fetch(entryMarkdownUrl(id, DEFAULT_LOCALE));
    if (fallback.ok) return fallback.text();
    throw new Error(
      `Failed to load What's New entry #${id} (${fallback.status})`
    );
  }
  throw new Error(`Failed to load What's New entry #${id} (${res.status})`);
};

export const useWhatsNewIndex = () =>
  useQuery({
    queryKey: ["whats-new", "index"],
    queryFn: fetchIndex,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    retry: 1,
  });

export const useWhatsNewEntry = (
  id: number | undefined,
  locale: WhatsNewLocale
) =>
  useQuery({
    queryKey: ["whats-new", "entry", id, locale],
    queryFn: () => fetchEntryContent(id as number, locale),
    enabled: typeof id === "number",
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    retry: 1,
  });
